// Bandeja de WhatsApp: lista de chats (izquierda) + conversación (derecha).
// En móvil: lista → conversación con «volver».
//
// Datos: GET /admin/whatsapp/app/chats (misma bandeja y privacidad de
// Conversaciones, solo WhatsApp de clientes). Refresco: cada 15 s con la
// pestaña visible y al volver a ella — sin infraestructura nueva.

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Hand, Inbox, MessageSquareText, Search, Sparkles, X } from 'lucide-react';

import { useModules } from '@/context/ModulesContext';
import { getChats, type WaChat, type WaOverview } from '../api';
import { Conversation, chatName } from './Conversation';
import { Avatar, listStamp, prettyPhone, STAGE_LABEL } from './shared';
import { WhatsAppGlyph } from './WhatsAppGlyph';

type Filter = 'all' | 'unread' | 'waiting' | 'suggestion';
type Notify = (type: 'success' | 'error' | 'info', title: string, message?: string) => void;

const POLL_MS = 15000;

function preview(raw?: string | null): string {
  const s = String(raw || '').trim();
  if (!s.startsWith('{')) return s.replace(/\[IMAGEN_ADJUNTA[^\]]*\]/g, '📷 Foto').replace(/\s+/g, ' ');
  try {
    const o = JSON.parse(s);
    const b = (o?.bloques || []).find((x: any) => x?.tipo === 'texto');
    return String(b?.contenido || o?.respuesta || '').replace(/\s+/g, ' ');
  } catch {
    return s;
  }
}

export function ChatsView({ overview, isMobile, notify, numberLabel }: {
  overview: WaOverview | null;
  isMobile: boolean;
  notify: Notify;
  numberLabel: string;
}) {
  const { isEnabled } = useModules();
  const canSeeLeads = (() => { try { return isEnabled('crm'); } catch { return false; } })();
  const [items, setItems] = useState<WaChat[]>([]);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [qDeb, setQDeb] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [activeId, setActiveId] = useState<string | null>(null);
  // Móvil: la conversación abierta puede ampliarse a pantalla completa.
  const [expanded, setExpanded] = useState(false);
  const inflight = useRef(false);

  useEffect(() => { const t = setTimeout(() => setQDeb(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const r = await getChats(qDeb || undefined);
      setItems(r.items || []);
      setStatus('ok');
      setError(null);
    } catch (e: any) {
      setStatus((s) => (s === 'ok' ? s : 'error'));
      setError(e?.message || 'No se pudieron cargar los chats');
    } finally {
      inflight.current = false;
    }
  }, [qDeb]);

  useEffect(() => {
    load();
    const id = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { window.clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);

  const counts = useMemo(() => ({
    all: items.length,
    unread: items.filter((c) => (c.unread_admin_count || 0) > 0).length,
    waiting: items.filter((c) => c.awaiting_reply).length,
    suggestion: items.filter((c) => !!c.pending_suggestion).length,
  }), [items]);

  const visible = useMemo(() => items.filter((c) => (
    filter === 'all' ? true
      : filter === 'unread' ? (c.unread_admin_count || 0) > 0
        : filter === 'waiting' ? !!c.awaiting_reply
          : !!c.pending_suggestion
  )), [items, filter]);

  const active = items.find((c) => c.id === activeId) || null;
  const leadHref = (c: WaChat) => {
    if (!canSeeLeads) return null;
    const term = c.person?.name || String(c.telefono || '').replace(/\D/g, '').slice(-10);
    return term ? `/m/crm/leads?buscar=${encodeURIComponent(term)}` : null;
  };

  // ↑/↓ recorren la lista; Enter abre (la fila es un botón).
  const listRef = useRef<HTMLUListElement | null>(null);
  const onListKey = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const btns = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('button[data-chat]') || []);
    const i = btns.findIndex((b) => b === document.activeElement);
    const next = btns[Math.max(0, Math.min(btns.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)))];
    if (next) { e.preventDefault(); next.focus(); }
  };

  const showList = !isMobile || !active;
  const showChat = !isMobile || !!active;
  // Pantalla completa solo aplica en móvil y con un chat abierto.
  const fullScreen = isMobile && !!active && expanded;

  // Al salir de móvil o cerrar el chat, se sale también de pantalla completa.
  useEffect(() => { if (!isMobile || !active) setExpanded(false); }, [isMobile, active]);

  // Pantalla completa: bloquea el scroll de la página de fondo y Escape regresa a la vista normal.
  useEffect(() => {
    if (!fullScreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // La página del shell (.o-page, z-index:1) crea su propio apilamiento y la
    // barra superior (z-40) quedaba ENCIMA de la conversación, tapando
    // «Reducir» y «Volver». Con esta marca deja de apilar (index.css).
    document.documentElement.dataset.pantallaCompleta = 'si';
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; delete document.documentElement.dataset.pantallaCompleta; document.removeEventListener('keydown', onKey); };
  }, [fullScreen]);

  const chip = (k: Filter, label: string, n: number, Icon?: typeof Inbox) => (
    <button
      key={k}
      type="button"
      onClick={() => setFilter(k)}
      aria-pressed={filter === k}
      className={[
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[12.5px] font-medium whitespace-nowrap transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]',
        filter === k ? 'wa-accent-soft border-transparent wa-accent-text' : 'border-border text-muted-foreground hover:text-foreground',
      ].join(' ')}
    >
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
      {label}{n > 0 && <span className="tabular-nums opacity-80">{n}</span>}
    </button>
  );

  return (
    <div className="flex h-full min-h-0">
      {showList && (
        <aside className={`flex min-h-0 flex-col border-border bg-card ${isMobile ? 'w-full' : 'w-[340px] xl:w-[380px] shrink-0 border-r'}`} aria-label="Chats de WhatsApp">
          <div className="space-y-2 px-3 pt-3 pb-2">
            <label className="flex items-center gap-2 rounded-xl bg-muted px-3 py-2 focus-within:ring-2 focus-within:ring-[hsl(var(--wa-accent))]">
              <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
              <span className="sr-only">Buscar chats</span>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre, teléfono o mensaje"
                className="w-full bg-transparent text-[14px] outline-none placeholder:text-muted-foreground" />
              {q && <button onClick={() => setQ('')} aria-label="Limpiar búsqueda" className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
            </label>
            <div className="no-scrollbar -mx-3 flex gap-1.5 overflow-x-auto px-3" role="group" aria-label="Filtrar chats">
              {chip('all', 'Todos', 0)}
              {chip('unread', 'No leídos', counts.unread)}
              {chip('waiting', 'Por responder', counts.waiting)}
              {chip('suggestion', 'Sugerencias', counts.suggestion, Sparkles)}
            </div>
          </div>

          <div className="wa-scroll flex-1 min-h-0 overflow-y-auto">
            {status === 'loading' && (
              <ul className="space-y-1 px-2" aria-hidden>
                {Array.from({ length: 7 }).map((_, i) => (
                  <li key={i} className="flex items-center gap-3 rounded-xl px-2 py-3">
                    <span className="h-11 w-11 rounded-full bg-muted animate-pulse" />
                    <span className="flex-1 space-y-2"><span className="block h-3 w-2/5 rounded bg-muted animate-pulse" /><span className="block h-3 w-4/5 rounded bg-muted animate-pulse" /></span>
                  </li>
                ))}
              </ul>
            )}
            {status === 'error' && <p role="alert" className="mx-3 rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
            {status === 'ok' && visible.length === 0 && (
              <div className="px-6 py-10 text-center">
                <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-muted"><MessageSquareText className="h-6 w-6 text-muted-foreground" aria-hidden /></span>
                <p className="text-[14px] font-medium">{items.length === 0 ? 'Aún no hay chats de WhatsApp' : 'Nada con este filtro'}</p>
                <p className="mt-1 text-[13px] text-muted-foreground">
                  {items.length === 0
                    ? `Cuando un cliente escriba a ${numberLabel || 'tu número'}, su conversación aparecerá aquí.`
                    : 'Prueba con «Todos» o cambia la búsqueda.'}
                </p>
              </div>
            )}
            {status === 'ok' && visible.length > 0 && (
              <ul ref={listRef} onKeyDown={onListKey} className="px-2 pb-2">
                {visible.map((c) => {
                  const name = chatName(c);
                  const sel = c.id === activeId;
                  const unread = c.unread_admin_count || 0;
                  const stage = c.person?.stage ? STAGE_LABEL[c.person.stage] || c.person.stage : null;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        data-chat
                        onClick={() => setActiveId(c.id)}
                        aria-current={sel ? 'true' : undefined}
                        className={[
                          'flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]',
                          sel ? 'bg-muted' : 'hover:bg-muted/60',
                        ].join(' ')}
                      >
                        <Avatar name={name} />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className={`truncate text-[15px] ${unread ? 'font-semibold' : 'font-medium'}`}>{name}</span>
                            {stage && <span className="shrink-0 rounded border border-border px-1 text-[10.5px] font-medium text-muted-foreground">{stage}</span>}
                            <time className={`ml-auto shrink-0 text-[12px] tabular-nums ${unread ? 'wa-accent-text font-semibold' : 'text-muted-foreground'}`}>{listStamp(c.last_message_at)}</time>
                          </span>
                          <span className="mt-0.5 flex items-center gap-1.5">
                            {c.agent_paused && <Hand className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-300" aria-label="Tienes el control" />}
                            {c.pending_suggestion && <Sparkles className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-300" aria-label="Sugerencia pendiente" />}
                            <span className={`truncate text-[13px] ${unread ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {c.person?.name && !c.last_message_preview ? prettyPhone(c.telefono) : preview(c.last_message_preview) || prettyPhone(c.telefono)}
                            </span>
                            {unread > 0 && (
                              <span className="wa-badge ml-auto grid h-5 min-w-5 shrink-0 place-items-center rounded-full px-1.5 text-[11px] font-bold tabular-nums" aria-label={`${unread} sin leer`}>{unread > 99 ? '99+' : unread}</span>
                            )}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      )}

      {showChat && (
        // Mismo contenedor, solo cambia la clase: al ampliar se conserva el scroll y lo escrito.
        <div className={fullScreen ? 'app-safe-full fixed inset-0 z-[112] bg-background' : 'flex-1 min-w-0 min-h-0'}>
          {active ? (
            <Conversation key={active.id} chat={active} isMobile={isMobile} notify={notify}
              expanded={fullScreen} onToggleExpand={() => setExpanded((v) => !v)}
              onBack={() => { setExpanded(false); setActiveId(null); }} onChanged={load} leadHref={leadHref(active)} />
          ) : (
            <EmptyChat overview={overview} />
          )}
        </div>
      )}
    </div>
  );
}

function EmptyChat({ overview }: { overview: WaOverview | null }) {
  return (
    <div className="wa-wallpaper grid h-full place-items-center p-8">
      <div className="max-w-sm text-center">
        <span className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full bg-card shadow-sm">
          <WhatsAppGlyph size={34} title={null} />
        </span>
        <h2 className="text-[17px] font-semibold">Las conversaciones de tus clientes, en un solo lugar</h2>
        <p className="mt-2 text-[13.5px] text-muted-foreground">
          Elige un chat para leerlo y responder. {overview?.org_mode === 'suggest'
            ? 'Tu agente está en «Solo sugiere»: te deja cada respuesta lista para enviar, editar o descartar.'
            : overview?.org_mode === 'off'
              ? 'Tu agente está apagado: aquí responde tu equipo.'
              : 'Tu agente responde solo; puedes tomar el control de cualquier conversación.'}
        </p>
      </div>
    </div>
  );
}
